import { supabase } from "@/integrations/supabase/client";

export type InternalTransferRow = {
  id: string;
  transfer_date: string;
  from_account_id: string;
  to_account_id: string;
  from_amount: number;
  to_amount: number;
  from_currency: string | null;
  to_currency: string | null;
  reference_number: string | null;
  description: string | null;
};

export type TransferKind = "transfer_ref" | "transfer_auto";

export type TransferSolvedPair = {
  transfer_id: string;
  bank_transaction_id: string;
  side: "from" | "to";
  score: 100;
  kind: TransferKind;
  reasons: string[];
  transfer: InternalTransferRow;
};

type BankTxLite = {
  id: string;
  bank_account_id: string;
  transaction_date: string;
  transaction_type: string | null;
  transaction_amount: number | null;
  transaction_amount_currency: string | null;
  transaction_id_reference: string | null;
};

const extractRefTokens = (s?: string | null): string[] => {
  if (!s) return [];
  const up = s.toUpperCase().replace(/[|,;/]+/g, " ");
  const hits = up.match(/[A-Z0-9]+(?:-[A-Z0-9]+)+|[A-Z0-9]{6,32}/g) ?? [];
  return Array.from(new Set(hits.filter((tok) => /\d/.test(tok))));
};

const dayKey = (s: string): string => {
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return s.slice(0, 10);
  return d.toISOString().slice(0, 10);
};

const dayDiff = (a: string, b: string) =>
  Math.round((Date.parse(dayKey(a)) - Date.parse(dayKey(b))) / 86400000);

const norm = (v: unknown) => String(v ?? "").trim().toLowerCase();

export async function fetchInternalTransfers(opts: {
  dateFrom: string;
  dateTo: string;
  daysWindow: number;
  bankAccountId?: string | null;
}): Promise<InternalTransferRow[]> {
  const from = new Date(`${opts.dateFrom}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - opts.daysWindow);
  const to = new Date(`${opts.dateTo}T00:00:00Z`);
  to.setUTCDate(to.getUTCDate() + opts.daysWindow);

  const fromIso = from.toISOString().slice(0, 10);
  const toIso = to.toISOString().slice(0, 10);

  let q = (supabase as any)
    .from("internal_transfers")
    .select(
      "id, transfer_date, from_account_id, to_account_id, from_amount, to_amount, from_currency, to_currency, reference_number, description"
    )
    .gte("transfer_date", fromIso)
    .lte("transfer_date", toIso);

  if (opts.bankAccountId) {
    q = q.or(
      `from_account_id.eq.${opts.bankAccountId},to_account_id.eq.${opts.bankAccountId}`
    );
  }

  q = q.limit(10000);

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as InternalTransferRow[];
}

export function matchInternalTransfers(opts: {
  transfers: InternalTransferRow[];
  bankTxs: BankTxLite[];
  daysWindow: number;
  amountTolerance: number;
  pctTolerance: number;
  lockedTx: Set<string>;
}): TransferSolvedPair[] {
  const { transfers, bankTxs, daysWindow, amountTolerance, pctTolerance, lockedTx } = opts;

  type Cand = {
    transfer: InternalTransferRow;
    side: "from" | "to";
    tx: BankTxLite;
    score: number;
    delta: number;
    dDiff: number;
    refHit: boolean;
  };

  const cands: Cand[] = [];

  for (const t of transfers) {
    for (const side of ["from", "to"] as const) {
      const accountId = side === "from" ? t.from_account_id : t.to_account_id;
      const amount = Number(side === "from" ? t.from_amount : t.to_amount) || 0;
      const currency = side === "from" ? t.from_currency : t.to_currency;
      const wantType = side === "from" ? "debit" : "credit";

      if (amount <= 0 || !accountId) continue;

      for (const tx of bankTxs) {
        if (lockedTx.has(tx.id)) continue;
        if (tx.bank_account_id !== accountId) continue;
        if (norm(tx.transaction_type) !== wantType) continue;

        const dd = Math.abs(dayDiff(tx.transaction_date, t.transfer_date));
        if (dd > daysWindow) continue;

        const txAmt = Math.abs(Number(tx.transaction_amount) || 0);
        const delta = Math.abs(txAmt - amount);
        const absTol = Math.max(amountTolerance * 3, 2);
        const relTol = Math.max(absTol, amount * pctTolerance);
        if (delta > relTol) continue;

        const tTokens = new Set(extractRefTokens(t.reference_number));
        const xTokens = extractRefTokens(tx.transaction_id_reference);
        const refHit = xTokens.some((x) => tTokens.has(x));

        const ccyMatch = !!(
          currency &&
          tx.transaction_amount_currency &&
          currency === tx.transaction_amount_currency
        );

        let score = 0;
        if (delta <= Math.max(amountTolerance, 1)) score += 60;
        else if (delta <= relTol) score += 50;
        if (ccyMatch) score += 10;
        if (dd === 0) score += 25;
        else if (dd === 1) score += 20;
        else score += 10;

        if (refHit) score = 100;

        cands.push({ transfer: t, side, tx, score, delta, dDiff: dd, refHit });
      }
    }
  }

  cands.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.delta !== b.delta) return a.delta - b.delta;
    return a.dDiff - b.dDiff;
  });

  const usedTx = new Set<string>();
  const usedSide = new Set<string>();
  const out: TransferSolvedPair[] = [];

  for (const c of cands) {
    if (usedTx.has(c.tx.id)) continue;
    const sideKey = `${c.transfer.id}:${c.side}`;
    if (usedSide.has(sideKey)) continue;

    const reasons: string[] = [];
    if (c.refHit) reasons.push("Reference id match");
    reasons.push(`internal_transfer_${c.side}`);
    reasons.push(`amount_delta=${c.delta.toFixed(2)}`);
    reasons.push(`date_diff_days=${c.dDiff}`);

    out.push({
      transfer_id: c.transfer.id,
      bank_transaction_id: c.tx.id,
      side: c.side,
      score: 100,
      kind: c.refHit ? "transfer_ref" : "transfer_auto",
      reasons,
      transfer: c.transfer,
    });

    usedTx.add(c.tx.id);
    usedSide.add(sideKey);
  }

  return out;
}
